import {useEffect, useMemo} from 'react';
import {RouterProvider} from 'react-router-dom';

import CssBaseline from '@mui/material/CssBaseline';
import {createTheme, ThemeProvider} from '@mui/material/styles';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';

import {AuthWrapper} from './AuthWrapper';
import {Toastify} from './modules/common/components/Toastify';
import {usePreferencesStore} from './modules/common/stores/preferencesStore';
import {router} from './router/router';
import {ThemeBase} from './theme/ThemeBase';
import {ThemeDarkPalette} from './theme/ThemeDarkPalette';
import {ThemeLightPalette} from './theme/ThemeLightPalette';
import {toast} from './ui-lib/components';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {retry: 2},
    mutations: {
      onError: (error: unknown): void => {
        const message =
          error instanceof Error ? error.message : 'Mutation failed';
        toast(message, 'error');
      },
    },
  },
});

function App(): JSX.Element {
  const themeMode = usePreferencesStore((state) => state.themeMode);
  useEffect(() => {
    const el = document.querySelector('#splash-screen');
    el?.remove();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        theme={useMemo(() => {
          const palette =
            themeMode === 'dark' ? ThemeDarkPalette : ThemeLightPalette;
          return createTheme({
            ...ThemeBase,
            palette,
          });
        }, [themeMode])}
      >
        <CssBaseline />
        <AuthWrapper>
          <RouterProvider router={router} />
          <Toastify />
        </AuthWrapper>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
